import useAuth from '@/hooks/useAuth';

const axiosInstance = useAuth.getAxiosInstance(`${process.env.VUE_APP_URL_GATEWAY}/v1/corporate`);

const corbinetApi = {
  requestTimeout: 30000,
  fetchFirmsUrl: "/firms",
  fetchCodesUrl: "/codes",
  saveCodeUrl: "/codes",
  fetchPhonesUrl: "/phones",
  savePhoneUrl: "/phones",
  deletePhoneUrl: "/phones",
  fetchTariffsUrl: "/tariffs",
  saveTariffUrl: "/tariffs",
  fetchCodePhoneTariffsUrl: "/code-phone-tariffs",
  saveCodePhoneTariffsUrl: "/code-phone-tariffs",
  fetchSchedulesUrl: "/sched",
  addSchedulesUrl: "/sched",
  deleteScheduleUrl: "/sched",
  fetchBoundedPointsUrl: "/bounded-points",
  fetchAutocompleteUrl: "/autocomplete",
  fetchLocationsUrl: "/points",
  addLocationUrl: "/points",
  deleteLocationUrl: "/points",

  /**
   * 
   * @throws Error
   */
  fetchFirms: async function () {
    // await new Promise(resolve => setTimeout(resolve, 2000));
    try {
      const response = await axiosInstance.get(this.fetchFirmsUrl, {}, { timeout: this.requestTimeout });
      return response.data.data;
    } catch (error) {
      throw new Error(this.resolveRestError(error));
    }
  },

  fetchCodes: async function (includeDeps = false) {
    try {
      const currentFirm = await this.getCurrentFirm()
      if (currentFirm === null) {
        throw new Error("no firm selected");
      }

      const response = await axiosInstance.get(this.fetchCodesUrl, {
        params: {
          "cid": currentFirm.cid,
          "filials": includeDeps ? 'true' : 'false',
          "t": this.requestTimeout, 
          // to avoid caching
          "s": new Date().getTime(),
        },
      }, { timeout: this.requestTimeout });
      return response.data.data;
    } catch (error) {
      throw new Error(this.resolveRestError(error));
    }
  },

  saveCode: async function (code) {
    try {
      const currentFirm = await this.getCurrentFirm()
      const reqBody = {
        "cid": currentFirm.cid,
        "code_id": code.code_id,
        "code_active": code.code_active,
        "code_name": code.code_name,
        "limit_mnth_active": code.limit_mnth_active,
        "limit_mnth_amt": Number(code.limit_mnth_amt),
        "limit_day_active": code.limit_day_active,
        "limit_day_amt": Number(code.limit_day_amt),
        "restrict_address_active": code.restrict_address_active,
        "restrict_tariff_active": code.restrict_tariff_active,
      }
      const response = await axiosInstance.put(this.fetchCodesUrl, reqBody, { timeout: this.requestTimeout });
      return response.data.data;
    } catch (error) {
      throw new Error(this.resolveRestError(error));
    }
  },

  fetchPhones: async function (codeId) {
    try {
      const currentFirm = await this.getCurrentFirm()
      const response = await axiosInstance.get(this.fetchPhonesUrl, {
        params: {
          "cid": currentFirm.cid,
          "code_id": codeId,
        },
      }, { timeout: this.requestTimeout });
      return response.data.data;
    } catch (error) {
      throw new Error(this.resolveRestError(error));
    }
  },

  savePhone: async function (codeId, phone) {
    try {
      const currentFirm = await this.getCurrentFirm()
      const reqBody = {
        "cid": currentFirm.cid,
        "code_id": new Number(codeId),
        "phone": phone.phone,
        "name": phone.name,
        "limit_mnth_active": phone.limit_mnth_active,
        "limit_mnth_amt": Number(phone.limit_mnth_amt),
        "limit_day_active": phone.limit_day_active,
        "limit_day_amt": Number(phone.limit_day_amt),
      }
      const response = await axiosInstance.put(this.savePhoneUrl, reqBody, { timeout: this.requestTimeout });
      return response.data.data;
    } catch (error) {
      throw new Error(this.resolveRestError(error));
    }
  },

  deletePhone: async function (codeId, phone) {
    try {
      const currentFirm = await this.getCurrentFirm()
      const response = await axiosInstance.delete(this.deletePhoneUrl, {
        params: {
          "cid": currentFirm.cid,
          "code_id": codeId,
          "phone": phone,
        },
      }, { timeout: this.requestTimeout });
      return response.data.data;
    } catch (error) {
      throw new Error(this.resolveRestError(error));
    }
  },

  getCurrentFirm: async function () {
    try {
      const currentFirm = JSON.parse(localStorage.getItem('currentFirm'));
      if (currentFirm) {
        return currentFirm;
      }
      const data = await this.fetchFirms();
      if (data.length === 0) {
        throw new Error("no firms available");
      }
      localStorage.setItem('currentFirm', JSON.stringify(data[0]));
      return data[0];
    } catch (error) {
      console.error(this.resolveRestError(error));
      return null;
    }
  },

  setCurrentFirm: async function (firm) {
    try {
      localStorage.setItem('currentFirm', JSON.stringify(firm));
    } catch (error) {
      console.error(this.resolveRestError(error));
    }
  },

  fetchTariffs: async function (codeId) {
    try {
      const currentFirm = await this.getCurrentFirm()
      const response = await axiosInstance.get(this.fetchTariffsUrl, {
        params: {
          "cid": currentFirm.cid,
          "code_id": codeId,
        },
      }, { timeout: this.requestTimeout });
      return response.data.data;
    } catch (error) {
      throw new Error(this.resolveRestError(error));
    }
  },

  /**
   * The value of tariffs_mask is computed by sum of power(2, ${tariff.id}) - e.g.: "tariffs_mask": 18
   * 
   * @param {string} codeId
   * @param {Object} tariffs
   * 
   * @throws Error
   * @returns {Promise<Object>}
   */
  saveTariffs: async function (codeId, tariffs) {
    try {
      const currentFirm = await this.getCurrentFirm()
      // convert tariffs to mask
      let mask = 0;
      for (let i = 0; i < tariffs.length; i++) {
        const tariff = tariffs[i];
        if (tariff.active) {
          mask += Math.pow(2, tariff.id);
        }
      }

      const reqBody = {
        "cid": currentFirm.cid,
        "code_id": new Number(codeId),
        "tariffs_mask": mask,
      }
      const response = await axiosInstance.put(this.saveTariffUrl, reqBody, { timeout: this.requestTimeout });
      return response.data.data;
    }
    catch (error) {
      throw new Error(this.resolveRestError(error));
    }
  },

  fetchCodePhoneTariffs: async function (codeId, phone) {
    try {
      const currentFirm = await this.getCurrentFirm()
      const response = await axiosInstance.get(this.fetchCodePhoneTariffsUrl, {
        params: {
          "cid": currentFirm.cid,
          "code_id": codeId,
          "phone": phone,
        },
      }, { timeout: this.requestTimeout });
      return response.data.data;
    } catch (error) {
      throw new Error(this.resolveRestError(error));
    }
  },

  saveCodePhoneTariffs: async function (codeId, phone, mask) {
    try {
      const currentFirm = await this.getCurrentFirm()
      const reqBody = {
        "cid": currentFirm.cid,
        "code_id": new Number(codeId),
        "phone": phone,
        "tariffs_mask": mask,
      }
      const response = await axiosInstance.put(this.saveCodePhoneTariffsUrl, reqBody, { timeout: this.requestTimeout });
      return response.data.data;
    } catch (error) {
      throw new Error(this.resolveRestError(error));
    }
  },

  fetchSchedules: async function (codeId) {
    try {
      const currentFirm = await this.getCurrentFirm()
      const response = await axiosInstance.get(this.fetchSchedulesUrl, {
        params: {
          "cid": currentFirm.cid,
          "code_id": codeId,
        },
      }, { timeout: this.requestTimeout });
      return response.data.data;
    } catch (error) {
      throw new Error(this.resolveRestError(error));
    }
  },

  saveSchedule: async function (codeId, schedule) {
    try {
      const currentFirm = await this.getCurrentFirm()
      const reqBody = {
        "cid": currentFirm.cid,
        "id": schedule.id,
        "code_id": new Number(codeId),
        "active": schedule.active,
        "days_mask": schedule.days_mask,
        "date_start": schedule.date_start,
        "date_end": schedule.date_end,
        "time_start": schedule.time_start,
        "time_end": schedule.time_end,
      }

      const response = await axiosInstance.put(this.fetchSchedulesUrl, reqBody, { timeout: this.requestTimeout });
      return response.data.data;
    } catch (error) {
      throw new Error(this.resolveRestError(error));
    }
  },

  addSchedule: async function (codeId, schedule) {
    try {
      const currentFirm = await this.getCurrentFirm()
      const reqBody = {
        "cid": currentFirm.cid,
        "code_id": codeId,
        "active": schedule.active,
        "days_mask": schedule.days_mask,
        "date_start": schedule.date_start,
        "date_end": schedule.date_end,
        "time_start": schedule.time_start,
        "time_end": schedule.time_end,
      }

      const response = await axiosInstance.post(this.addSchedulesUrl, reqBody, { timeout: this.requestTimeout });
      return response.data.data;
    } catch (error) {
      throw new Error(this.resolveRestError(error));
    }
  },

  deleteSchedule: async function (scheduleId) {
    try {
      const currentFirm = await this.getCurrentFirm()
      const response = await axiosInstance.delete(this.deleteScheduleUrl, {
        params: {
          "cid": currentFirm.cid,
          "id": scheduleId,
        },
      }, { timeout: this.requestTimeout });

      return response.data.data;
    } catch (error) {
      throw new Error(this.resolveRestError(error));
    }
  },

  fetchBoundedPointsList: async function (p) {
    try {
      const currentFirm = await this.getCurrentFirm()
      const response = await axiosInstance.get(this.fetchBoundedPointsUrl, {
        params: {
          "cid": currentFirm.cid,
          "center_lat": p.center_lat,
          "center_lon": p.center_lon,
          "radius": p.radius,
        },
      }, { timeout: this.requestTimeout });

      const res = response.data.data;
      res.forEach(p => {
        p.address = p.Label ? `${p.Label}, ${p.Street} ${p.House}` : `${p.Street} ${p.House}`;
      });

      return res;
    } catch (error) {
      throw new Error(this.resolveRestError(error));
    }
  },

  fetchAutocomplete: async function (input) {
    try {
      const response = await axiosInstance.get(this.fetchAutocompleteUrl, {
        params: {
          "input": input,
          "lang": "az",
        },
      }, { timeout: this.requestTimeout });

      const res = response.data.data;

      if (!res || res.length === 0) {
        return [];
      }
      
      // res.forEach(p => {
      //   p.House = "";
      //   p.Street = p.candidate || p.candidateAZ;
      //   p.Label = p.description || p.descriptionAZ;
      //   p.Lat = p.lat;
      //   p.Lon = p.lon;
      //   p.Uid = p.ID;
      //   p.address = `${p.Label}, ${p.Street}`;
      // });

      // return first 30
      return res.slice(0, 30).map(p => ({
        House: "",
        Street: p.candidate || p.candidateAZ,
        Label: p.description || p.descriptionAZ,
        Lat: p.lat,
        Lon: p.lon,
        Uid: p.ID,
        address: `${p.description || p.descriptionAZ}, ${p.candidate || p.candidateAZ}`,
      }));

    } catch (error) {
      throw new Error(this.resolveRestError(error));
    }
  },

  fetchLocationsList: async function (p) {
    try {
      // await new Promise(resolve => setTimeout(resolve, 2000));
      const currentFirm = await this.getCurrentFirm()
      const response = await axiosInstance.get(this.fetchLocationsUrl, {
        params: {
          "cid": currentFirm.cid,
          "code_id": p.code_id,
        },
      }, { timeout: this.requestTimeout });
      return response.data.data;
    } catch (error) {
      throw new Error(this.resolveRestError(error));
    }
  },

  addLocation: async function (p) {
    try {
      const currentFirm = await this.getCurrentFirm()
      const reqBody = {
        "cid": currentFirm.cid,
        "code_id": Number(p.code_id),
        "address": p.address,
        "lat": p.lat,
        "lon": p.lon,
        "type": Number(p.type),
        "radius": Number(p.radius),
      }
      const response = await axiosInstance.post(this.addLocationUrl, reqBody, { timeout: this.requestTimeout });
      return response.data.data;
    } catch (error) {
      throw new Error(this.resolveRestError(error));
    }
  },

  deleteLocation: async function (p) {
    try {
      const currentFirm = await this.getCurrentFirm()
      const response = await axiosInstance.delete(this.deleteLocationUrl, {
        params: {
          "cid": currentFirm.cid,
          "code_id": p.code_id,
          "point_id": p.location_id,
        },
      }, { timeout: this.requestTimeout });

      return response.data.data;
    } catch (error) {
      throw new Error(this.resolveRestError(error));
    }
  },

  resolveRestError: function (err) {
    if (err.response && err.response.code == 401) {
      return "auth required...";
    }
    if (err.response && err.response.data.error) {
      return err.response.data.error.message
    }
    return err.message || "unresolved error"
  },
}

///////////////////////////////////////////////////////////////////////////////

export default corbinetApi;